import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Link2, Server, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DialogCloseButton } from '@/components/ui/dialog-close-button';
import { BackButton } from '@/components/ui/back-button';
import { PageShell, PageTopRow, ContentCard } from '@/components/ui/page-shell';
import { getProductName } from '@/lib/config';
import { toast } from 'sonner';
import {
  Square3Stack3DIcon,
  AtSymbolIcon,
  InboxArrowDownIcon,
} from '@heroicons/react/24/outline';

export default function AddEmailAccountPage() {
  const navigate = useNavigate();
  const [isManualSetupOpen, setIsManualSetupOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [emailError, setEmailError] = useState('');
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message?: string; error?: string } | null>(null);
  const [imapErrors, setImapErrors] = useState({
    username: '',
    password: '',
    host: '',
    port: '',
  });
  const [smtpErrors, setSmtpErrors] = useState({
    host: '',
    port: '',
  });
  
  // Form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    imapUsername: '',
    imapPassword: '',
    imapHost: '',
    imapPort: '993',
    smtpHost: '',
    smtpPort: '587',
    useSameCredentials: true,
  });

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear email error when user starts typing
    if (field === 'email' && emailError) {
      setEmailError('');
    }
    // Clear IMAP field errors when user starts typing
    if (field === 'imapUsername' && imapErrors.username) {
      setImapErrors(prev => ({ ...prev, username: '' }));
    }
    if (field === 'imapPassword' && imapErrors.password) {
      setImapErrors(prev => ({ ...prev, password: '' }));
    }
    if (field === 'imapHost' && imapErrors.host) {
      setImapErrors(prev => ({ ...prev, host: '' }));
    }
    if (field === 'imapPort' && imapErrors.port) {
      setImapErrors(prev => ({ ...prev, port: '' }));
    }
    // Clear SMTP field errors when user starts typing
    if (field === 'smtpHost' && smtpErrors.host) {
      setSmtpErrors(prev => ({ ...prev, host: '' }));
    }
    if (field === 'smtpPort' && smtpErrors.port) {
      setSmtpErrors(prev => ({ ...prev, port: '' }));
    }
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleNext = () => {
    if (wizardStep === 1) {
      // Validate step 1 fields
      if (!formData.firstName || !formData.lastName || !formData.email) {
        if (!formData.email) {
          setEmailError('Email is required');
        } else {
          setEmailError('');
        }
        return;
      }
      // Validate email format
      if (!validateEmail(formData.email)) {
        setEmailError('Please enter a valid email address');
        return;
      }
      setEmailError('');
      setWizardStep(2);
    } else if (wizardStep === 2) {
      // Validate IMAP fields before proceeding to Step 3
      const errors = {
        username: !formData.imapUsername ? 'IMAP Username is required' : '',
        password: !formData.imapPassword ? 'IMAP Password is required' : '',
        host: !formData.imapHost ? 'IMAP Host is required' : '',
        port: !formData.imapPort ? 'IMAP Port is required' : '',
      };
      
      setImapErrors(errors);
      
      if (!formData.imapUsername || !formData.imapPassword || !formData.imapHost || !formData.imapPort) {
        return;
      }
      
      // Auto-derive SMTP host from IMAP host if not already set
      if (!formData.smtpHost && formData.imapHost) {
        const derivedSmtpHost = formData.imapHost.replace('imap.', 'smtp.');
        setFormData(prev => ({ ...prev, smtpHost: derivedSmtpHost }));
      }
      
      setWizardStep(3);
    }
  };

  const handleBack = () => {
    if (wizardStep === 2) {
      setWizardStep(1);
    } else if (wizardStep === 3) {
      setWizardStep(2);
    }
  };


  const handleTestConnection = async () => {
    // Validate SMTP fields (we're on step 3)
    const errors = {
      host: !formData.smtpHost ? 'SMTP Host is required' : '',
      port: !formData.smtpPort ? 'SMTP Port is required' : '',
    };
    
    setSmtpErrors(errors);
    
    if (!formData.smtpHost || !formData.smtpPort) {
      return;
    }

    // Test connection
    setIsTestingConnection(true);
    setTestResult(null);

    try {
      const response = await fetch('/api/email-accounts/test-connection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          imapHost: formData.imapHost,
          imapPort: formData.imapPort,
          smtpHost: formData.smtpHost,
          smtpPort: formData.smtpPort,
          imapUsername: formData.imapUsername,
          imapPassword: formData.imapPassword,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Connection test successful - show success message
        setTestResult({ 
          success: true, 
          message: data.message || 'Connection test successful! Your email settings are correct.' 
        });
      } else {
        // Connection test failed, show error
        setTestResult({ 
          success: false, 
          error: data.error || data.details || 'Connection test failed' 
        });
      }
    } catch (error) {
      setTestResult({
        success: false,
        error: error instanceof Error ? error.message : 'Failed to test connection',
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const handleSubmit = async () => {
    // Validate SMTP fields
    const errors = {
      host: !formData.smtpHost ? 'SMTP Host is required' : '',
      port: !formData.smtpPort ? 'SMTP Port is required' : '',
    };
    
    setSmtpErrors(errors);
    
    if (!formData.smtpHost || !formData.smtpPort) {
      return;
    }

    const smtpPort = parseInt(formData.smtpPort, 10);
    const useSSL = smtpPort === 465 || parseInt(formData.imapPort, 10) === 993;

    try {
      // Create display name from first and last name
      const displayName = formData.firstName || formData.lastName 
        ? `${formData.firstName} ${formData.lastName}`.trim()
        : '';

      const response = await fetch('/api/email-accounts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          displayName: displayName,
          imapHost: formData.imapHost,
          imapPort: parseInt(formData.imapPort, 10),
          imapUsername: formData.imapUsername,
          imapPassword: formData.imapPassword,
          smtpHost: formData.smtpHost,
          smtpPort: smtpPort,
          username: formData.imapUsername,
          password: formData.imapPassword,
          useSSL: useSSL,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Account creation failed
        setTestResult({
          success: false,
          error: data.error || data.details || 'Failed to create email account',
        });
        return;
      }

      // Account created successfully
      setIsManualSetupOpen(false);
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        imapUsername: '',
        imapPassword: '',
        imapHost: '',
        imapPort: '993',
        smtpHost: '',
        smtpPort: '587',
        useSameCredentials: true,
      });
      setWizardStep(1);
      setTestResult(null);
      
      // Show success toast
      toast.success('Email account added successfully!', {
        description: `Account for ${formData.email} has been connected.`,
      });
      
      navigate('/dashboard/email-accounts');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create email account';
      setTestResult({
        success: false,
        error: errorMessage,
      });
      // Show error toast
      toast.error('Failed to create email account', {
        description: errorMessage,
      });
    }
  };

  const handleDialogClose = (open: boolean) => {
    setIsManualSetupOpen(open);
    if (!open) {
      // Reset form when closing
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        imapUsername: '',
        imapPassword: '',
        imapHost: '',
        imapPort: '993',
        smtpHost: '',
        smtpPort: '587',
        useSameCredentials: true,
      });
      setWizardStep(1);
      setEmailError('');
      setTestResult(null);
      setImapErrors({ username: '', password: '', host: '', port: '' });
      setSmtpErrors({ host: '', port: '' });
    }
  };

  // The standard page shell: the header row on the page inset like every
  // other page, and the options as white boxes on light grey. The header
  // shares the grey and the panel drops its top rule, so the whole page is
  // one grey surface.
  return (
    <PageShell className="bg-surface-panel">
      <PageTopRow className="gap-4">
        <BackButton
          href="/dashboard/email-accounts"
          tooltip="Back to Email Accounts"
        />
        <h1 className="text-lg font-medium">Add Email Account</h1>
      </PageTopRow>

      <ContentCard tinted flush className="border-t-0">
      <div className="w-full flex justify-center pb-20" style={{ contain: 'layout paint' }}>
        <div className="w-full max-w-4xl space-y-6">
          {/* Automatic Setup Card */}
          <Card className="bg-card shadow-none border border-panel-border">
            <CardHeader>
              <h2 className="text-lg font-medium">
                Automatic Setup{' '}
                <span className="text-success-foreground font-normal">(Recommended)</span>
              </h2>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Three Columns */}
              <div className="grid grid-cols-3 gap-0 pt-4">
                <div className="flex flex-col items-start gap-3">
                  <Square3Stack3DIcon className="h-12 w-12 text-content-body" />
                  <p className="text-sm text-content-body">We set up your accounts</p>
                </div>
                <div className="flex flex-col items-start gap-3">
                  <AtSymbolIcon className="h-12 w-12 text-content-body" />
                  <p className="text-sm text-content-body">You choose the domains</p>
                </div>
                <div className="flex flex-col items-start gap-3">
                  <InboxArrowDownIcon className="h-12 w-12 text-content-body" />
                  <p className="text-sm text-content-body">Deliverability optimized</p>
                </div>
              </div>

              {/* Button at bottom right */}
              <div className="pt-4 flex justify-end -mr-3 -mb-3">
                <Button
                  variant="primary"
                  onClick={() => navigate('/dashboard/email-accounts/dfy')}
                >
                  Continue Automatic Setup
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* OAuth Connect Card */}
          <Card className="bg-card shadow-none border border-panel-border">
            <CardHeader>
              <h2 className="text-lg font-medium">Connect with OAuth</h2>
            </CardHeader>
            <CardContent className="space-y-6">
              <ul className="list-disc list-inside space-y-2 text-sm text-content-body">
                <li>Connect your Google or Microsoft account securely</li>
                <li>No passwords needed — uses official OAuth authentication</li>
              </ul>

              <div className="flex justify-end gap-3 pt-4 -mr-3 -mb-3">
                <Button variant="secondary" asChild>
                  <a href="/api/email-accounts/connect/google">Connect Google</a>
                </Button>
                <Button variant="secondary" asChild>
                  <a href="/api/email-accounts/connect/microsoft">Connect Microsoft</a>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Manual Setup Card */}
          <Card className="bg-card shadow-none border border-panel-border">
            <CardHeader>
              <h2 className="text-lg font-medium">Connect Existing Accounts Manually</h2>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Bullet List */}
              <ul className="list-disc list-inside space-y-2 text-sm text-content-body">
                <li>Connect any IMAP or SMTP email provider</li>
                <li>Sync up replies in your {getProductName()} mailbox</li>
              </ul>

              {/* Button bottom right */}
              <div className="flex justify-end pt-4 -mr-3 -mb-3">
                <Dialog open={isManualSetupOpen} onOpenChange={handleDialogClose}>
                  <DialogTrigger asChild>
                    <Button variant="primary">
                      Connect Account
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-[500px]">
                    {wizardStep === 1 && (
                      <>
                        <DialogHeader className="pt-0">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 align-middle items-center mt-1">
                              <Link2 className="h-8 w-8 mt-1 flex-shrink-0" />
                              <div>
                                <DialogTitle>Connect Any Account</DialogTitle>
                                <DialogDescription className="mt-1">IMAP / SMTP</DialogDescription>
                              </div>
                            </div>
                            <DialogCloseButton onClick={() => handleDialogClose(false)} />
                          </div>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="flex flex-col">
                              <Label htmlFor="firstName">First Name</Label>
                              <Input
                                id="firstName"
                                value={formData.firstName}
                                onChange={(e) => handleInputChange('firstName', e.target.value)}
                                className="mt-1 w-full bg-input-bg"
                                placeholder="Enter first name"
                              />
                            </div>
                            <div className="flex flex-col">
                              <Label htmlFor="lastName">Last Name</Label>
                              <Input
                                id="lastName"
                                value={formData.lastName}
                                onChange={(e) => handleInputChange('lastName', e.target.value)}
                                className="mt-1 w-full bg-input-bg"
                                placeholder="Enter last name"
                              />
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="email">Email *</Label>
                            <Input
                              id="email"
                              type="email"
                              value={formData.email}
                              onChange={(e) => handleInputChange('email', e.target.value)}
                              className={`mt-1 w-full ${emailError ? 'outline outline-2 outline-ring-error-strong outline-offset-2 bg-input-bg' : 'bg-input-bg'}`}
                              placeholder="Enter email address"
                              required
                            />
                            {emailError && (
                              <p className="text-sm text-danger-text mt-1">{emailError}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-4 -mr-2 -mb-2">
                          <Button variant="primary" onClick={handleNext}>
                            Continue
                          </Button>
                        </div>
                      </>
                    )}
                    {wizardStep === 2 && (
                      <>
                        <DialogHeader className="pt-0">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 align-middle items-center mt-2">
                              <Server className="h-8 w-8 mt-1 flex-shrink-0" />
                              <div>
                                <DialogTitle>IMAP Settings</DialogTitle>
                                <DialogDescription className="mt-1">For receiving emails</DialogDescription>
                              </div>
                            </div>
                            <DialogCloseButton onClick={() => handleDialogClose(false)} />
                          </div>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div>
                            <Label htmlFor="imapUsername">IMAP Username *</Label>
                            <Input
                              id="imapUsername"
                              value={formData.imapUsername}
                              onChange={(e) => handleInputChange('imapUsername', e.target.value)}
                              className={`mt-1 w-full bg-input-bg ${imapErrors.username ? 'outline outline-2 outline-ring-error-strong outline-offset-2' : ''}`}
                              placeholder="Enter IMAP username"
                              required
                            />
                            {imapErrors.username && (
                              <p className="text-sm text-danger-text mt-1 ml-3">{imapErrors.username}</p>
                            )}
                          </div>
                          <div>
                            <Label htmlFor="imapPassword">IMAP Password *</Label>
                            <Input
                              id="imapPassword"
                              type="password"
                              value={formData.imapPassword}
                              onChange={(e) => handleInputChange('imapPassword', e.target.value)}
                              className={`mt-1 w-full bg-input-bg ${imapErrors.password ? 'outline outline-2 outline-ring-error-strong outline-offset-2' : ''}`}
                              placeholder="Enter IMAP password"
                              required
                            />
                            {imapErrors.password && (
                              <p className="text-sm text-danger-text mt-1 ml-3">{imapErrors.password}</p>
                            )}
                          </div>
                          <div className="grid grid-cols-[2fr_1fr] gap-4">
                            <div className="flex flex-col">
                              <Label htmlFor="imapHost">IMAP Host *</Label>
                              <Input
                                id="imapHost"
                                value={formData.imapHost}
                                onChange={(e) => handleInputChange('imapHost', e.target.value)}
                                className={`mt-1 w-full bg-input-bg ${imapErrors.host ? 'outline outline-2 outline-ring-error-strong outline-offset-2' : ''}`}
                                placeholder="e.g., imap.gmail.com"
                                required
                              />
                              {imapErrors.host && (
                                <p className="text-sm text-danger-text mt-1 ml-3">{imapErrors.host}</p>
                              )}
                            </div>
                            <div className="flex flex-col">
                              <Label htmlFor="imapPort">IMAP Port *</Label>
                              <Input
                                id="imapPort"
                                type="number"
                                value={formData.imapPort}
                                onChange={(e) => handleInputChange('imapPort', e.target.value)}
                                className={`mt-1 w-full bg-input-bg ${imapErrors.port ? 'outline outline-2 outline-ring-error-strong outline-offset-2' : ''}`}
                                placeholder="e.g., 993"
                                required
                              />
                              {imapErrors.port && (
                                <p className="text-sm text-danger-text mt-1 ml-3">{imapErrors.port}</p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-end gap-3 pt-4 -mr-2 -mb-2">
                          <Button variant="secondary" onClick={handleBack}>
                            <ChevronLeft className="h-4 w-4 -ml-1" />
                            Back
                          </Button>
                          <Button variant="primary" onClick={handleNext}>
                            Continue
                            <ChevronRight className="h-4 w-4 -mr-1" />
                          </Button>
                        </div>
                      </>
                    )}
                    {wizardStep === 3 && (
                      <>
                        <DialogHeader className="pt-0">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 align-middle items-center mt-2">
                              <Send className="h-8 w-8 mt-1 flex-shrink-0" />
                              <div>
                                <DialogTitle>SMTP Settings</DialogTitle>
                                <DialogDescription className="mt-1">For sending emails</DialogDescription>
                              </div>
                            </div>
                            <DialogCloseButton onClick={() => handleDialogClose(false)} />
                          </div>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <p className="text-sm text-content-secondary">
                            SMTP is required to send emails from your account. The credentials from IMAP will be used for authentication.
                          </p>
                          <div className="grid grid-cols-[2fr_1fr] gap-4">
                            <div className="flex flex-col">
                              <Label htmlFor="smtpHost">SMTP Host *</Label>
                              <Input
                                id="smtpHost"
                                value={formData.smtpHost}
                                onChange={(e) => handleInputChange('smtpHost', e.target.value)}
                                className={`mt-1 w-full bg-input-bg ${smtpErrors.host ? 'outline outline-2 outline-ring-error-strong outline-offset-2' : ''}`}
                                placeholder="e.g., smtp.gmail.com"
                                required
                              />
                              {smtpErrors.host && (
                                <p className="text-sm text-danger-text mt-1 ml-3">{smtpErrors.host}</p>
                              )}
                            </div>
                            <div className="flex flex-col">
                              <Label htmlFor="smtpPort">SMTP Port *</Label>
                              <Input
                                id="smtpPort"
                                type="number"
                                value={formData.smtpPort}
                                onChange={(e) => handleInputChange('smtpPort', e.target.value)}
                                className={`mt-1 w-full bg-input-bg ${smtpErrors.port ? 'outline outline-2 outline-ring-error-strong outline-offset-2' : ''}`}
                                placeholder="e.g., 587"
                                required
                              />
                              {smtpErrors.port && (
                                <p className="text-sm text-danger-text mt-1 ml-3">{smtpErrors.port}</p>
                              )}
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Common SMTP ports: 587 (TLS), 465 (SSL), 25 (unencrypted)
                          </p>
                          {testResult && (
                            <div className={`p-3 rounded-lg ${testResult.success ? 'bg-success-surface border border-success-border' : 'bg-danger-surface border border-danger-border'}`}>
                              <p className={`text-sm ${testResult.success ? 'text-success-foreground-strong' : 'text-danger-foreground-strong'}`}>
                                {testResult.success ? testResult.message : testResult.error}
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="flex justify-end gap-3 pt-4 -mr-2 -mb-2">
                          <Button variant="secondary" onClick={handleBack}>
                            <ChevronLeft className="h-4 w-4 -ml-1" />
                            Back
                          </Button>
                          <Button 
                            variant="secondary" 
                            onClick={handleTestConnection}
                            disabled={isTestingConnection}
                          >
                            {isTestingConnection ? 'Testing...' : 'Test Connection'}
                          </Button>
                          <Button 
                            variant="primary" 
                            onClick={handleSubmit}
                            disabled={isTestingConnection}
                          >
                            Connect Account
                          </Button>
                        </div>
                      </>
                    )}
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      </ContentCard>
    </PageShell>
  );
}
